import * as React from "react";

import { cn } from "../../lib/utils";

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
  disabled?: boolean;
  pageLabel?: (currentPage: number, totalPages: number) => string;
  previousLabel?: string;
  nextLabel?: string;
  iconClassName?: string;
  previousIconClassName?: string;
  nextIconClassName?: string;
};

const defaultPageLabel = (currentPage: number, totalPages: number) =>
  `Page ${currentPage} of ${totalPages}`;

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className,
  disabled = false,
  pageLabel = defaultPageLabel,
  previousLabel = "Previous page",
  nextLabel = "Next page",
  iconClassName,
  previousIconClassName,
  nextIconClassName,
}: PaginationProps) => {
  const safeTotal = Math.max(totalPages, 0);
  const clampedCurrent = safeTotal
    ? Math.min(Math.max(currentPage, 1), safeTotal)
    : 1;
  const canGoPrev = clampedCurrent > 1;
  const canGoNext = safeTotal > 0 && clampedCurrent < safeTotal;

  return (
    <div
      className={cn(
        "w-full inline-flex items-center justify-between gap-3 px-3 py-2",
        className
      )}
      role="navigation"
      aria-label="Pagination"
    >
      <button
        type="button"
        className="rounded-lg border border-black bg-white px-2.5 py-2 text-[#101828] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] transition-all duration-150 active:translate-y-[2px] disabled:opacity-40 disabled:translate-y-0"
        aria-label={previousLabel}
        disabled={!canGoPrev || disabled}
        onClick={() => onPageChange(clampedCurrent - 1)}
      >
        <ArrowLeftIcon
          className={cn("h-[14px] w-[14px]", iconClassName, previousIconClassName)}
        />
      </button>
      <span className="w-[17ch] shrink-0 overflow-hidden text-ellipsis whitespace-nowrap text-center text-[13px] font-semibold tabular-nums text-[#344054]">
        {pageLabel(clampedCurrent, safeTotal)}
      </span>
      <button
        type="button"
        className="rounded-lg border border-black bg-white px-2.5 py-2 text-[#101828] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] transition-all duration-150 active:translate-y-[2px] disabled:opacity-40 disabled:translate-y-0"
        aria-label={nextLabel}
        disabled={!canGoNext || disabled}
        onClick={() => onPageChange(clampedCurrent + 1)}
      >
        <ArrowRightIcon
          className={cn("h-[14px] w-[14px]", iconClassName, nextIconClassName)}
        />
      </button>
    </div>
  );
};

const ArrowLeftIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M12.5 6.66659H0.833344M0.833344 6.66659L6.66668 12.4999M0.833344 6.66659L6.66668 0.833252"
      stroke="currentColor"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M0.833328 6.66659H12.5M12.5 6.66659L6.66666 0.833252M12.5 6.66659L6.66666 12.4999"
      stroke="currentColor"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export { Pagination };
export type { PaginationProps };
